import React, { useEffect, useState } from "react";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const PROGRAM_ORDER = ["meditation_enlightenment", "bliss_path", "aura_night", "clarity_call", "one_to_one"];
const UNIT = { meditation_enlightenment: "Session", bliss_path: "Batch", aura_night: "Journey", clarity_call: "Slot", one_to_one: "Slot" };
const RANGE_PROGRAMS = ["bliss_path", "aura_night"];

const EMPTY = {
  program_type: "meditation_enlightenment", title: "", track: "both", description: "",
  start_date: "", end_date: "", start_time: "", end_time: "",
  timezone: "Asia/Kolkata", capacity: 50, price_inr: 499, price_usd: 9, status: "active",
  zoom_link: "", coach_name: "", mode: "zoom", markets: ["in", "com"],
};

export default function ProgramsPage() {
  const [items, setItems] = useState([]);
  const [timezones, setTimezones] = useState(["Asia/Kolkata", "UTC"]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/programs"); setItems(data); } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    api.get("/admin/master/timezones").then(({ data }) => setTimezones(data)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggleMarket = (m) => setForm(s => {
    const cur = s.markets || ["in", "com"];
    return { ...s, markets: cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m] };
  });

  const startCreate = (programType) => {
    setForm({ ...EMPTY, program_type: programType }); setEditingId(null); setOpen(true);
  };
  const startEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditingId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.start_date || !form.start_time) { toast.error("Title, start date and start time are required"); return; }
    try {
      const payload = { ...form, capacity: Number(form.capacity), price_inr: Number(form.price_inr), price_usd: Number(form.price_usd) };
      if (editingId) await api.patch(`/admin/programs/${editingId}`, payload);
      else await api.post("/admin/programs", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/programs/${id}`); load(); };

  const isRange = RANGE_PROGRAMS.includes(form.program_type);
  const unit = UNIT[form.program_type] || "Slot";

  return (
    <div className="space-y-8 fade-up" data-testid="admin-programs">
      <div>
        <h1 className="font-serif text-4xl">Programs</h1>
        <p className="text-sm text-[#7E736E]">Manage each program's sessions, batches and journeys from within the program below. Prices set here drive the public site.</p>
      </div>

      {PROGRAM_ORDER.map(pt => {
        const slots = items.filter(i => i.program_type === pt);
        const u = UNIT[pt] || "Slot";
        return (
          <div key={pt} className="space-y-3" data-testid={`program-group-${pt}`}>
            <div className="flex items-center justify-between border-b border-[#EBE5D9] pb-2">
              <h2 className="font-serif text-2xl">{PROGRAM_LABELS[pt]}</h2>
              <button onClick={() => startCreate(pt)} className="btn-primary text-sm" data-testid={`add-${pt}`}>+ Add {u}</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slots.map(p => (
                <div key={p.id} className="soft-card !p-5" data-testid={`slot-${p.id}`}>
                  <div className="font-serif text-lg">{p.title}</div>
                  <div className="text-xs text-[#7E736E] mt-1">
                    {RANGE_PROGRAMS.includes(pt) && p.end_date ? `${p.start_date} – ${p.end_date}` : p.start_date} · {p.start_time}{p.end_time ? `–${p.end_time}` : ""} · {p.timezone}
                  </div>
                  <div className="text-xs mt-3">₹{p.price_inr} · ${p.price_usd} · {p.capacity} seats</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    <span className="badge-soft bg-[#F9E8C0] text-[#7A5C1F]">{p.status}</span>
                    {(p.markets || ["in", "com"]).includes("in") && <span className="badge-soft bg-[#D4E4D3] text-[#2F4F2F]" data-testid={`badge-in-${p.id}`}>.in</span>}
                    {(p.markets || ["in", "com"]).includes("com") && <span className="badge-soft bg-[#D6E4F2] text-[#2F4A6E]" data-testid={`badge-com-${p.id}`}>.com</span>}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => startEdit(p)} className="btn-ghost text-xs" data-testid={`edit-${p.id}`}>Edit</button>
                    <button onClick={() => del(p.id)} className="text-xs text-red-700 underline ml-auto" data-testid={`delete-${p.id}`}>Delete</button>
                  </div>
                </div>
              ))}
              {slots.length === 0 && <div className="soft-card !p-5 text-sm text-[#7E736E]">No {u.toLowerCase()}s yet. Use "+ Add {u}".</div>}
            </div>
          </div>
        );
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="slot-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingId ? "Edit" : "New"} {unit} · {PROGRAM_LABELS[form.program_type]}</DialogTitle>
            <DialogDescription className="text-xs text-[#7E736E]">Set the price and domain visibility — these reflect directly on the public site.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1 sm:col-span-2"><Label>Title</Label><Input value={form.title} onChange={e => upd("title", e.target.value)} data-testid="slot-title" /></div>
            <div className="space-y-1"><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={e => upd("start_date", e.target.value)} data-testid="slot-start-date" /></div>
            {isRange && <div className="space-y-1"><Label>End date</Label><Input type="date" value={form.end_date || ""} onChange={e => upd("end_date", e.target.value)} data-testid="slot-end-date" /></div>}
            <div className="space-y-1"><Label>Start time</Label><Input type="time" value={form.start_time || ""} onChange={e => upd("start_time", e.target.value)} data-testid="slot-start-time" /></div>
            <div className="space-y-1"><Label>End time</Label><Input type="time" value={form.end_time || ""} onChange={e => upd("end_time", e.target.value)} data-testid="slot-end-time" /></div>
            <div className="space-y-1">
              <Label>Timezone</Label>
              <Select value={form.timezone} onValueChange={v => upd("timezone", v)}>
                <SelectTrigger data-testid="slot-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>{timezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => upd("capacity", e.target.value)} data-testid="slot-capacity" /></div>
            <div className="space-y-1"><Label>Price ₹ (India / INR)</Label><Input type="number" value={form.price_inr} onChange={e => upd("price_inr", e.target.value)} data-testid="slot-price-inr" /></div>
            <div className="space-y-1"><Label>Price $ (Global / USD)</Label><Input type="number" value={form.price_usd} onChange={e => upd("price_usd", e.target.value)} data-testid="slot-price-usd" /></div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => upd("status", v)}>
                <SelectTrigger data-testid="slot-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "draft", "closed", "cancelled"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Domain visibility</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => toggleMarket("in")} data-testid="vis-in"
                  className={`flex-1 rounded-full px-4 py-2 text-sm border ${(form.markets || []).includes("in") ? "bg-terracotta text-white border-terracotta" : "border-[#EBE5D9] text-[#5C5955]"}`}>
                  🇮🇳 India (.in · INR)
                </button>
                <button type="button" onClick={() => toggleMarket("com")} data-testid="vis-com"
                  className={`flex-1 rounded-full px-4 py-2 text-sm border ${(form.markets || []).includes("com") ? "bg-terracotta text-white border-terracotta" : "border-[#EBE5D9] text-[#5C5955]"}`}>
                  🌍 Global (.com · USD)
                </button>
              </div>
            </div>
            <div className="space-y-1 sm:col-span-2"><Label>Zoom link</Label><Input value={form.zoom_link || ""} onChange={e => upd("zoom_link", e.target.value)} /></div>
          </div>
          <button onClick={save} className="btn-primary w-full mt-2" data-testid="slot-save">Save</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
